import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export type RfqExportRow = {
  rfqNumber: string;
  title: string;
  vesselName: string;
  imoNumber?: string;
  supplyPort?: string;
  brand?: string;
  category?: string;
  status: string;
  createdAt: string;
  dueDate?: string;
  /** Vendor portal: customer / sender org */
  senderName?: string;
};

export type RfqExportFormat = 'excel' | 'pdf';

function stamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function cell(value?: string | null): string {
  const text = String(value ?? '').trim();
  return text || 'N/A';
}

/** jsPDF Helvetica is WinAnsi — strip characters that corrupt the PDF. */
function pdfSafe(value: string): string {
  return cell(value)
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u00A0/g, ' ')
    .replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, '');
}

function headersFor(includeSender: boolean, includeDueDate: boolean): string[] {
  const headers = [
    ...(includeSender ? ['Customer / Sender'] : []),
    'RFQ Number',
    'Title',
    'Vessel',
    'IMO',
    'Supply Port',
    'Brand',
    'Category',
    'Status',
    'Created At',
    ...(includeDueDate ? ['Due Date'] : []),
  ];
  return headers;
}

function rowValues(row: RfqExportRow, includeSender: boolean, includeDueDate: boolean): string[] {
  return [
    ...(includeSender ? [cell(row.senderName)] : []),
    cell(row.rfqNumber),
    cell(row.title),
    cell(row.vesselName),
    cell(row.imoNumber),
    cell(row.supplyPort),
    cell(row.brand),
    cell(row.category),
    cell(row.status),
    cell(row.createdAt),
    ...(includeDueDate ? [cell(row.dueDate)] : []),
  ];
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^\w.-]+/g, '_').replace(/_+/g, '_');
}

function layoutFlags(rows: RfqExportRow[]) {
  return {
    includeSender: rows.some((r) => Boolean(r.senderName && r.senderName.trim())),
    includeDueDate: rows.some((r) => Boolean(r.dueDate && r.dueDate.trim())),
  };
}

/** Build a clean .xlsx workbook of RFQ list rows and trigger download. */
export function downloadRfqDataExcel(
  rows: RfqExportRow[],
  options?: { filenamePrefix?: string; sheetName?: string },
): void {
  if (!rows.length) {
    throw new Error('No RFQ data available to export');
  }

  const { includeSender, includeDueDate } = layoutFlags(rows);
  const headers = headersFor(includeSender, includeDueDate);
  const data = rows.map((r) => rowValues(r, includeSender, includeDueDate));

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

  ws['!cols'] = headers.map((h, i) => {
    const maxLen = Math.max(h.length, ...data.map((row) => String(row[i] ?? '').length));
    return { wch: Math.min(Math.max(maxLen + 2, 14), 45) };
  });

  const lastCol = headers.length - 1;
  const lastRow = data.length;
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastRow, c: lastCol } }),
  };
  ws['!views'] = [{ state: 'frozen', ySplit: 1, topLeftCell: 'A2', activeCell: 'A2' }];

  XLSX.utils.book_append_sheet(wb, ws, (options?.sheetName || 'RFQs').slice(0, 31));
  const filename = sanitizeFilename(
    `${options?.filenamePrefix || 'RFQ_Export'}_${stamp()}.xlsx`,
  );
  XLSX.writeFile(wb, filename, { bookType: 'xlsx', compression: true });
}

/** Build a landscape PDF table of RFQ list rows and trigger download. */
export function downloadRfqDataPdf(
  rows: RfqExportRow[],
  options?: { filenamePrefix?: string; title?: string; subtitle?: string },
): void {
  if (!rows.length) {
    throw new Error('No RFQ data available to export');
  }

  const { includeSender, includeDueDate } = layoutFlags(rows);
  const headers = headersFor(includeSender, includeDueDate).map(pdfSafe);
  const body = rows.map((r) =>
    rowValues(r, includeSender, includeDueDate).map(pdfSafe),
  );

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const title = pdfSafe(options?.title || 'RFQ & Quotation Export');
  const subtitle = pdfSafe(options?.subtitle || `Generated ${new Date().toLocaleString()}`);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(15, 23, 42);
  doc.text(title, 14, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(subtitle, 14, 22);
  doc.text(`${rows.length} record${rows.length === 1 ? '' : 's'}`, 14, 27);

  autoTable(doc, {
    startY: 32,
    head: [headers],
    body,
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 7.5,
      cellPadding: 2,
      overflow: 'linebreak',
      valign: 'middle',
      textColor: [30, 41, 59],
      lineColor: [226, 232, 240],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [0, 136, 204],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7.5,
      halign: 'left',
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    margin: { left: 10, right: 10, top: 14, bottom: 16 },
  });

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() - 10,
      doc.internal.pageSize.getHeight() - 8,
      { align: 'right' },
    );
  }

  const filename = sanitizeFilename(
    `${options?.filenamePrefix || 'RFQ_Export'}_${stamp()}.pdf`,
  );
  doc.save(filename);
}
